/** Gabarits opérationnels acceptés par eFashion, en centimètres et kilogrammes. */
export interface CarrierDimensionLimits {
  carrierName: string;
  longueur: number;
  largeur: number;
  hauteur: number;
  poids: number;
  validationMode: "volume" | "sides";
}

const STANDARD_LIMITS = {
  longueur: 80,
  largeur: 50,
  hauteur: 30,
  poids: 25,
  validationMode: "volume" as const,
};

const EUROPEAN_COUNTRY_CODES = new Set([
  "AL", "AD", "AT", "BE", "BA", "BG", "HR", "CY", "CZ", "DK", "EE", "FI",
  "FR", "DE", "GR", "HU", "IS", "IE", "IT", "XK", "LV", "LI", "LT", "LU",
  "MT", "MD", "MC", "ME", "NL", "MK", "NO", "PL", "PT", "RO", "SM", "RS",
  "SK", "SI", "ES", "SE", "CH", "UA", "GB", "VA", "BY",
]);

const EUROPE_LIMITS = { ...STANDARD_LIMITS, hauteur: 40, poids: 30 };

const LIMITS: Record<string, CarrierDimensionLimits> = {
  gls: { carrierName: "GLS", ...STANDARD_LIMITS },
  dpd: { carrierName: "DPD", ...STANDARD_LIMITS },
  ups: { carrierName: "UPS", ...STANDARD_LIMITS },
  gpx: { carrierName: "GPX", ...STANDARD_LIMITS },
  bollore: { carrierName: "Bolloré", ...STANDARD_LIMITS },
  ocx: { carrierName: "OCX", ...STANDARD_LIMITS },
  chronopost: { carrierName: "Chronopost", ...STANDARD_LIMITS },
  fedex: { carrierName: "FedEx", ...STANDARD_LIMITS },
  colissimo: {
    carrierName: "Colissimo",
    longueur: 60,
    largeur: 40,
    hauteur: 40,
    poids: 25,
    validationMode: "sides",
  },
};

const ID_TO_CARRIER: Record<number, string> = {
  1: "colissimo",
  4: "colissimo",
  8: "colissimo",
  12: "colissimo",
  16: "colissimo",
  18: "colissimo",
  2736: "colissimo",
  2737: "colissimo",
  2746: "colissimo",
  2924: "colissimo",
  2925: "colissimo",
  10: "dpd",
  13: "dpd",
  2743: "dpd",
  14: "gls",
  15: "gls",
  2742: "gls",
  2931: "gls",
  6: "gpx",
  11: "gpx",
  2649: "gpx",
  2915: "gpx",
  2916: "bollore",
  7: "ocx",
  17: "ocx",
  2741: "ocx",
  19: "ups",
  2744: "ups",
  2745: "ups",
  2923: "ups",
  2932: "ups",
  2729: "chronopost",
  2730: "chronopost",
  2731: "chronopost",
  2732: "chronopost",
  2747: "chronopost",
  2917: "chronopost",
  2918: "chronopost",
  2919: "chronopost",
  2920: "chronopost",
  2921: "chronopost",
  2922: "chronopost",
  2748: "fedex",
  2926: "fedex",
  2927: "fedex",
  2928: "fedex",
  2929: "fedex",
  2930: "fedex",
};

const DEFAULT: CarrierDimensionLimits = {
  carrierName: "ce transporteur",
  ...STANDARD_LIMITS,
};

export function getCarrierDimensionLimits(
  id: number | null | undefined,
  countryCode?: string | null,
  postalCode?: string | null,
): CarrierDimensionLimits {
  if (id == null) return DEFAULT;
  const key = ID_TO_CARRIER[id];
  const code = countryCode?.trim().toUpperCase();
  const postal = postalCode?.replace(/\s/g, "").toUpperCase() ?? "";
  const isOverseas =
    (code === "FR" && /^(97|98)/.test(postal)) ||
    (code === "ES" && /^(35|38|51|52)/.test(postal)) ||
    (code === "PT" && /^9/.test(postal));
  if (
    key &&
    ["chronopost", "gls", "dpd"].includes(key) &&
    code &&
    EUROPEAN_COUNTRY_CODES.has(code) &&
    !isOverseas
  ) {
    return {
      carrierName: LIMITS[key].carrierName,
      ...EUROPE_LIMITS,
    };
  }
  return key ? LIMITS[key] ?? DEFAULT : DEFAULT;
}

export function validateColisDimensions(
  longueur: number,
  largeur: number,
  hauteur: number,
  limits: CarrierDimensionLimits,
): string | null {
  if (limits.validationMode === "volume") {
    const volume = longueur * largeur * hauteur;
    const maxVolume = limits.longueur * limits.largeur * limits.hauteur;
    if (volume > maxVolume) {
      return `volume ${Math.round(volume)} cm³ (maximum ${Math.round(maxVolume)} cm³, soit ${limits.longueur} × ${limits.largeur} × ${limits.hauteur} cm)`;
    }
    return null;
  }

  const volume = longueur * largeur * hauteur;
  const maxVolume = limits.longueur * limits.largeur * limits.hauteur;
  if (volume > maxVolume) {
    return `volume ${Math.round(volume)} cm³ (maximum ${Math.round(maxVolume)} cm³, soit ${limits.longueur} × ${limits.largeur} × ${limits.hauteur} cm)`;
  }

  const dimensions = [longueur, largeur, hauteur].sort((a, b) => b - a);
  const maximums = [limits.longueur, limits.largeur, limits.hauteur].sort(
    (a, b) => b - a,
  );
  if (dimensions.some((dimension, index) => dimension > maximums[index])) {
    return `dimensions ${longueur} × ${largeur} × ${hauteur} cm (maximum ${limits.longueur} × ${limits.largeur} × ${limits.hauteur} cm)`;
  }
  return null;
}

export function getDimensionLimitsHint(id: number | null | undefined, countryCode?: string | null, postalCode?: string | null): string {
  const limits = getCarrierDimensionLimits(id, countryCode, postalCode);
  if (limits.validationMode === "sides") {
    return `Dimensions maximales pour ${limits.carrierName} : ${limits.longueur} × ${limits.largeur} × ${limits.hauteur} cm — ${limits.poids} kg par colis.`;
  }
  return `Volume maximal pour ${limits.carrierName} : ${limits.longueur} × ${limits.largeur} × ${limits.hauteur} cm, soit ${limits.longueur * limits.largeur * limits.hauteur} cm³ — ${limits.poids} kg par colis.`;
}
